import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Space, Tag, Alert, Empty, Spin, Button, Input, InputNumber,
  Tooltip, message,
} from 'antd';
import { SaveOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../libs/api';
import {
  INK, NEUTRAL, OVER, UNDER, BUDGET_BG, BUDGET_INK, FAINT, fmtMoney, FRAME,
} from './shared';

/**
 * Cash that never reaches the profit and loss.
 *
 * BAS and GST remittances, superannuation, loan drawdowns and repayments, asset
 * purchases, transfers between entities. Its own tab because it is a different
 * kind of number from opex: no budget exists for a loan repayment and none
 * should, so there is nothing in Opex Control to inherit and the forward months
 * are typed here instead.
 *
 * In and out stay in separate boxes rather than one signed field. A drawdown
 * and a repayment in the same month net to nothing while both really happened,
 * and a forecast that shows nothing there is a forecast that has quietly
 * dropped two real cash movements.
 */

const cellWidth = 118;

export default function BsMovements({ entity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});        // rowKey -> month -> { out, in }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/cashflow/bs', { params: { entity }, timeout: 180000 });
      const d = r.data.data;
      setData(d);
      /**
       * Open on what is saved; where nothing is saved, on the recent average.
       *
       * These are the repeating obligations — superannuation, a loan
       * repayment, a quarterly BAS — and the honest first guess at next month
       * is what the last three did. Averaging rather than repeating handles
       * the quarterly ones correctly over a quarter: a payment that lands in
       * one month of three contributes a third to each, which is the right
       * total and the wrong timing, and the timing is the part a person can
       * see and fix.
       *
       * The three complete months only. This month is part way through, and
       * including it would drag every forecast down by however much of it has
       * not happened yet.
       */
      const complete = (d.actualMonths || []).filter((m) => m < d.thisMonth);
      const average = (row, field) => {
        if (!complete.length) return null;
        const total = complete.reduce((t, m) => t + (row.actual?.[m]?.[field] || 0), 0);
        if (!total) return null;
        return Math.round(total / complete.length);
      };

      const seed = {};
      for (const row of d.rows) {
        const avgOut = average(row, 'out');
        const avgIn = average(row, 'in');
        seed[row.key] = {};
        for (const m of d.forecastMonths) {
          const savedCell = row.forecast?.[m];
          seed[row.key][m] = savedCell
            ? { out: savedCell.out ?? null, in: savedCell.in ?? null }
            : { out: avgOut, in: avgIn };
        }
      }
      setDraft(seed);
      setNote('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the balance sheet movements');
      setData(null);
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const setCell = (rowKey, month, field, value) => setDraft((d) => ({
    ...d,
    [rowKey]: {
      ...(d[rowKey] || {}),
      [month]: { ...((d[rowKey] || {})[month] || {}), [field]: value },
    },
  }));

  /**
   * Copy the last complete month across every forward month of a row.
   *
   * Most of what is on this tab repeats — superannuation, a loan repayment, a
   * quarterly BAS. Typing the same number four times is how one of the four
   * ends up different by accident.
   */
  const repeatRow = (row) => {
    const lastActual = [...data.actualMonths].reverse()
      .map((m) => row.actual?.[m]).find((x) => x && (x.out || x.in));
    if (!lastActual) {
      message.info('Nothing in the last three months to repeat for this account');
      return;
    }
    setDraft((d) => ({
      ...d,
      [row.key]: Object.fromEntries(data.forecastMonths.map((m) => [m, {
        out: lastActual.out || null,
        in: lastActual.in || null,
      }])),
    }));
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    return data.rows.some((row) => data.forecastMonths.some((m) => {
      const d = draft[row.key]?.[m] || {};
      const s = row.forecast?.[m] || {};
      return Number(d.out || 0) !== Number(s.out || 0) || Number(d.in || 0) !== Number(s.in || 0);
    }));
  }, [draft, data]);

  const save = async () => {
    setSaving(true);
    try {
      const months = {};
      for (const m of data.forecastMonths) {
        const accounts = [];
        for (const row of data.rows) {
          const d = draft[row.key]?.[m] || {};
          const out = Number(d.out) || 0;
          const inn = Number(d.in) || 0;
          // A row with nothing in it is not saved. An explicit zero and "no
          // movement expected" are the same statement here, and storing every
          // untouched account would bury the handful that were actually set.
          if (!out && !inn) continue;
          accounts.push({
            accountCode: row.accountCode,
            accountName: row.accountName,
            klass: row.klass,
            type: row.type,
            // Whether this line also lands in the profit and loss. Saved with
            // the row so the statements do not have to re-derive it from an
            // account that may not have moved since.
            affects: row.affects,
            out,
            in: inn,
          });
        }
        months[m] = { outputs: { accounts } };
      }
      const r = await api.post('/api/cashflow/save', {
        entity, kind: 'bs', months, note: note || null,
      });
      message.success(`Saved ${r.data.data.saved.length} months`);
      await load();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not save');
    }
    setSaving(false);
  };

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!data) {
    return (
      <Card size="small">
        <Spin spinning={loading}>
          <Empty description={loading ? 'Reading bank transactions…' : 'No data'} />
        </Spin>
      </Card>
    );
  }

  const { months, forecastMonths } = data;

  const columns = [
    {
      title: 'Account', dataIndex: 'accountName', width: 230, fixed: 'left',
      render: (v, r) => (
        <div>
          <div style={{ color: INK }}>{v}</div>
          <div style={{ fontSize: 11, color: NEUTRAL }}>
            {/* No point printing the code twice when the name is the code —
                which is what happens when the chart of accounts is missing. */}
            {r.accountCode && r.accountCode !== v ? r.accountCode : ''}
            <Tag style={{ marginLeft: 6 }}>{r.type || r.klass}</Tag>
            {r.affects === 'pl' && (
              <Tooltip title="A receipt here is income with no invoice behind it, so it lands in other income on the statements as well as in cash.">
                <Tag color="blue">P&amp;L</Tag>
              </Tooltip>
            )}
            <Tooltip title="Which part of the cashflow statement this account's movements land in">
              <Tag color={r.bucket === 'investing' ? 'purple' : (r.bucket === 'financing' ? 'geekblue' : 'default')}>
                {r.bucket}
              </Tag>
            </Tooltip>
          </div>
        </div>
      ),
    },
    ...FRAME.map((f) => {
      const m = months[f.idx] || null;
      const editable = f.kind === 'budget';
      return {
        key: f.key,
        width: editable ? 150 : cellWidth,
        align: editable ? 'left' : 'right',
        title: (
          <div style={{ lineHeight: 1.25 }}>
            <div>{m ? dayjs(m).format('MMM YY') : '—'}</div>
            <div style={{ fontSize: 10, fontWeight: 400, color: editable ? BUDGET_INK : NEUTRAL }}>
              {f.kind === 'mtd' ? 'MTD actual' : (editable ? 'forecast' : 'actual')}
            </div>
          </div>
        ),
        onHeaderCell: () => ({ style: editable ? { background: BUDGET_BG } : undefined }),
        onCell: () => ({ style: editable ? { background: BUDGET_BG } : undefined }),
        render: (_, row) => {
          if (!m) return <span style={{ color: FAINT }}>—</span>;
          if (!editable) {
            const a = row.actual?.[m];
            if (!a || (!a.out && !a.in)) return <span style={{ color: FAINT }}>—</span>;
            return (
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                {a.out ? <div style={{ color: OVER }}>out {fmtMoney(a.out)}</div> : null}
                {a.in ? <div style={{ color: UNDER }}>in {fmtMoney(a.in)}</div> : null}
              </div>
            );
          }
          const d = draft[row.key]?.[m] || {};
          return (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <InputNumber
                size="small" placeholder="out" prefix="↑"
                value={d.out} min={0} style={{ width: '100%' }}
                onChange={(v) => setCell(row.key, m, 'out', v)}
              />
              <InputNumber
                size="small" placeholder="in" prefix="↓"
                value={d.in} min={0} style={{ width: '100%' }}
                onChange={(v) => setCell(row.key, m, 'in', v)}
              />
            </Space>
          );
        },
      };
    }),
    {
      title: '', key: 'repeat', width: 56, fixed: 'right',
      render: (_, row) => (
        <Tooltip title="Repeat the most recent actual across all four forecast months">
          <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => repeatRow(row)} />
        </Tooltip>
      ),
    },
  ];

  const monthTotal = (m, which) => data.rows.reduce((t, row) => {
    if (forecastMonths.includes(m)) {
      const d = draft[row.key]?.[m] || {};
      return t + (Number(d[which]) || 0);
    }
    return t + (row.actual?.[m]?.[which] || 0);
  }, 0);

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        {data.bankError && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                Bank transactions unavailable — {data.bankError}. The forecast columns are
                unaffected; the actual columns are empty rather than zero.
              </span>
            )}
          />
        )}
        {data.chartError && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                The chart of accounts could not be read — {data.chartError}. Every row below is
                therefore labelled with its account code instead of its name, and classified as
                Unclassified. The amounts are right; only the labels are missing. Refreshing
                usually fixes it.
              </span>
            )}
          />
        )}
        {(data.bankTruncated || data.bankUnclassified > 0) && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                {data.bankTruncated && 'The bank read hit its page limit, so some movements may be missing. '}
                {data.bankUnclassified > 0 && `${fmtMoney(data.bankUnclassified)} sits on lines with no account code and cannot be classified.`}
              </span>
            )}
          />
        )}

        <Card
          size="small"
          title="Balance sheet movements"
          extra={(
            <Space>
              <Tag color="green">cash basis</Tag>
              {dirty && <Tag color="orange">unsaved</Tag>}
              <Button
                type="primary" size="small" icon={<SaveOutlined />}
                loading={saving} disabled={!dirty} onClick={save}
              >
                save forecast
              </Button>
            </Space>
          )}
        >
          <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 10 }}>
            Forecast months open on the average of the three complete months to their left, or on
            what was saved if anything has been. A quarterly payment therefore shows as a third in
            every month — right over the quarter, wrong in each month, and the timing is the part
            worth correcting by hand.
            <br />
            Everything the other two tabs do not cover. Mostly cash that never reaches the
            P&amp;L — BAS and GST, superannuation, loans, asset purchases, transfers between
            entities — plus receipts on revenue accounts, which are income that never had an
            invoice and which GP Control therefore does not forecast. Those carry a{' '}
            <Tag color="blue" style={{ marginLeft: 0 }}>P&amp;L</Tag> tag and flow into other
            income on the statements. No budget applies to any of it, so the forward months are
            typed here rather than inherited from Opex Control. Out and in are kept apart because
            a drawdown and a repayment in the same month net to nothing while both really happened.
          </div>
          <Table
            size="small"
            rowKey="key"
            dataSource={data.rows}
            columns={columns}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            scroll={{ x: 230 + 4 * cellWidth + 4 * 150 + 56 }}
            locale={{ emptyText: 'No balance sheet cash movements in the last three months' }}
            summary={() => (
              <Table.Summary.Row style={{ background: '#fafcfd' }}>
                <Table.Summary.Cell index={0}><b>All accounts</b></Table.Summary.Cell>
                {FRAME.map((f, i) => {
                  const m = months[f.idx];
                  const out = m ? monthTotal(m, 'out') : 0;
                  const inn = m ? monthTotal(m, 'in') : 0;
                  return (
                    <Table.Summary.Cell key={f.key} index={i + 1} align="right">
                      <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                        {out ? <div style={{ color: OVER }}>{fmtMoney(out)}</div> : null}
                        {inn ? <div style={{ color: UNDER }}>{fmtMoney(inn)}</div> : null}
                        {!out && !inn ? <span style={{ color: FAINT }}>—</span> : null}
                      </div>
                    </Table.Summary.Cell>
                  );
                })}
                <Table.Summary.Cell index={FRAME.length + 1} />
              </Table.Summary.Row>
            )}
          />
          <div style={{ marginTop: 10 }}>
            <Input
              size="small"
              placeholder="Why this change? (kept with the saved version)"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              style={{ maxWidth: 420 }}
            />
          </div>
          <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
            <PlusOutlined style={{ marginRight: 4 }} />
            Only accounts that have moved in the last three months are listed — an account that has
            never moved has no row to forecast on. If you need one that is not here, put a
            transaction through it in Xero and it will appear.
          </div>
        </Card>
      </Space>
    </Spin>
  );
}
